import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'

import { filterObjectEmptyValues } from '../../../../../../helpers/common'

import { updateLineItem, clearUpdateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { selectedTikTokAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { TIKTOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { LANGUAGES } from '../../../../../ReusableFormFields/LanguagesFields/fields'
import { tikTokLanguagesList } from '../../../../../../constants/selectLists/languagesList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

const LanguagesSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const { id: tiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const initialValues = useMemo(
    () => ({
      [LANGUAGES]: tikTokLanguagesList.filter(item => lineItem[LANGUAGES].includes(item.value))
    }),
    [lineItem]
  )

  const handleSubmit = useCallback(
    values => {
      // as we use PUT for update tiktok campaign, we should pass full data in payload
      dispatch(
        updateLineItem(
          {
            ...filterObjectEmptyValues(lineItem),
            account: tiktokAdAccountId,
            [LANGUAGES]: values[LANGUAGES].map(item => item.value),
            meta: {
              [HISTORY_META_EDIT_SECTION]: title
            }
          },
          lineItem.id,
          TIKTOK_PLATFORM,
          {
            requestMethod: 'PUT'
          }
        )
      )
    },
    [dispatch, lineItem, tiktokAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values } = formik

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated || manualSuccessSubmit}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <MultiSelectBox
        placeholder="Add a Language"
        name={LANGUAGES}
        options={tikTokLanguagesList}
        value={values[LANGUAGES]}
        setFieldValue={setFieldValue}
      />
    </EditForm>
  )
}

export default LanguagesSectionForm
